import { inject, Injectable, isDevMode } from "@angular/core";

import { LgTranslateService } from "@logex/framework/lg-localization";
import { LgPromptDialog, LgSnackbarService } from "@logex/framework/ui-core";
import { ApplicationEventTracerHub } from "@logex/framework/core";

/**
 * Error tracking entry with timestamp for deduplication
 */
interface TrackedError {
    guid: string;
    timestamp: number;
    url?: string | undefined;
    status?: number | undefined;
}

/**
 * Service for tracking network errors (network failures, OIDC errors).
 * Generates unique GUIDs, logs via ApplicationEventTracerHub, and handles user-facing recovery.
 * Handles error storms with deduplication (5-second cooldown).
 *
 * Recovery flow: on the first network error, the service auto-reloads the page (one-shot
 * via sessionStorage guard). After reload, a grace period determines the outcome:
 * - No further errors → persistent info snackbar ("page was reloaded").
 * - Errors persist → dialog with human-readable message, "Reload" button, and reference
 *   codes (GUIDs) shown as secondary detail for support.
 *
 * Callers may pass `{ silent: true }` to log the error without triggering reload or dialog
 * (e.g., intermediate OIDC retry attempts that are handled by the auth system).
 *
 * This service is specifically for network/connectivity failures, not application bugs.
 * For general application error handling, see lg-error-handler.
 */
@Injectable({ providedIn: "root" })
export class LgNetworkErrorService {
    private _tracerHub = inject(ApplicationEventTracerHub);
    private _promptDialog = inject(LgPromptDialog);
    private _snackbar = inject(LgSnackbarService);
    private _lgTranslate = inject(LgTranslateService);

    private _trackedErrors: TrackedError[] = [];
    private _cooldownTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private _isModalVisible = false;

    private _isPostReloadMonitoring = false;
    private _postReloadGraceTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private _retryInFlightCount = 0;
    private _gracePending = false;

    private static readonly COOLDOWN_MS = 5000;
    private static readonly ERROR_RETENTION_MS = 10000;
    private static readonly MAX_DISPLAYED_GUIDS = 3;
    private static readonly POST_RELOAD_GRACE_MS = 15_000;
    private static readonly SESSION_KEY_RELOAD_REASON = "lg_network_error_reload";

    constructor() {
        this._checkPostReloadState();
    }

    /**
     * Track a network error. Generates GUID, logs via tracer hub, handles deduplication.
     * This method never throws - errors during tracking are logged in dev mode but suppressed in production.
     *
     * On the first error in a page session, triggers auto-reload. If errors persist after
     * reload, a user-friendly dialog is shown with reference codes and a "Reload" button.
     *
     * @param error The error object or message
     * @param context Additional context (url, status code, etc.)
     * @param options Additional options
     * @param options.silent When true, the error is logged but does not trigger reload or dialog.
     *                       Use for intermediate retry errors that are being handled by the caller.
     * @returns The generated GUID, or null if tracking fails
     */
    trackError(
        error: Error | string,
        context?: { url?: string; status?: number },
        options?: { silent?: boolean }
    ): string | null {
        try {
            const guid = this._generateGuid();
            const timestamp = Date.now();

            // Always log to console with GUID (even in production) for debugging
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`[Network Error] GUID: ${guid}`, {
                error: errorMessage,
                url: context?.url,
                status: context?.status,
                timestamp: new Date(timestamp).toISOString()
            });

            // Log via tracer hub immediately (always log, even if modal suppressed)
            this._logToTracerHub(guid, error, context);

            // Silent mode: log only, no reload or dialog
            if (options?.silent) {
                return guid;
            }

            // Add to tracked errors
            const trackedError: TrackedError = {
                guid,
                timestamp,
                url: context?.url,
                status: context?.status
            };
            this._trackedErrors.push(trackedError);

            // Clean up old errors
            this._cleanupOldErrors();

            // If we're in post-reload monitoring and new errors arrive, cancel grace period
            // and let the normal modal flow handle it
            if (this._isPostReloadMonitoring) {
                this._cancelPostReloadGracePeriod();
            }

            // Auto-reload on first error (one-shot guard via sessionStorage)
            if (this._shouldAutoReload()) {
                this._performReload();
                return guid;
            }

            // Handle deduplication and modal display
            this._scheduleModalDisplay();

            return guid;
        } catch (trackingError) {
            // Never throw from trackError - this could break the application
            if (isDevMode()) {
                console.error("Network error tracking failed:", trackingError);
            }
            return null;
        }
    }

    private _getRecentGuids(): string[] {
        this._cleanupOldErrors();
        return this._trackedErrors.map(e => e.guid);
    }

    /**
     * Called by LgNetworkErrorInterceptor when an idempotent status-0 retry cycle begins.
     * Increments the in-flight counter during post-reload monitoring so that the grace
     * timer defers its success action rather than expiring mid-retry.
     *
     * OIDC coupling note: this method is intentionally NOT called from OidcAuthService.
     * OIDC silent-renew failures use trackError({silent:true}) only and must not delay
     * the post-reload success snackbar. The grace extension is therefore keyed exclusively
     * to network-layer retry cycles initiated by this interceptor.
     */
    notifyRetryStarted(): void {
        if (this._isPostReloadMonitoring) {
            this._retryInFlightCount++;
        }
    }

    /**
     * Called by LgNetworkErrorInterceptor when the retry observable terminates (success
     * or final exhaustion). If the grace timer fired while retries were in flight and no
     * non-silent error cancelled it, fires the deferred success snackbar now.
     *
     * Must be called AFTER the non-silent trackError (if any) so that
     * _cancelPostReloadGracePeriod has already cleared _gracePending before this check.
     */
    notifyRetryCompleted(): void {
        this._retryInFlightCount = Math.max(0, this._retryInFlightCount - 1);
        if (this._gracePending && this._retryInFlightCount === 0) {
            this._gracePending = false;
            this._isPostReloadMonitoring = false;
            this._clearReloadFlag();
            this._showPostReloadInfoSnackbar();
        }
    }

    /**
     * One-shot guard: only auto-reload if no prior reload attempt exists in sessionStorage.
     * The flag stays set during post-reload monitoring and is cleared only after the
     * grace period resolves (success → snackbar, or failure → dialog dismissed).
     */
    private _shouldAutoReload(): boolean {
        return !sessionStorage.getItem(LgNetworkErrorService.SESSION_KEY_RELOAD_REASON);
    }

    private _performReload(): void {
        sessionStorage.setItem(
            LgNetworkErrorService.SESSION_KEY_RELOAD_REASON,
            JSON.stringify({ reason: "network-error", timestamp: Date.now() })
        );
        this._reloadWindow();
    }

    /** @protected — overridable in tests to avoid triggering actual navigation */
    protected _reloadWindow(): void {
        window.location.reload();
    }

    private _checkPostReloadState(): void {
        const reloadData = sessionStorage.getItem(LgNetworkErrorService.SESSION_KEY_RELOAD_REASON);
        if (!reloadData) {
            return;
        }

        // Keep the flag set — it prevents another auto-reload during the grace period.
        // It will be cleared when the grace period resolves.
        this._isPostReloadMonitoring = true;

        // Start grace period: if no new errors arrive, show info snackbar.
        // If a retry cycle is in flight when the timer fires, defer success until
        // the cycle completes (notifyRetryCompleted will finish the work).
        this._postReloadGraceTimeoutId = setTimeout(() => {
            this._postReloadGraceTimeoutId = null;
            if (this._retryInFlightCount > 0) {
                this._gracePending = true;
                return;
            }
            this._isPostReloadMonitoring = false;
            this._clearReloadFlag();
            this._showPostReloadInfoSnackbar();
        }, LgNetworkErrorService.POST_RELOAD_GRACE_MS);
    }

    private _cancelPostReloadGracePeriod(): void {
        if (this._postReloadGraceTimeoutId !== null) {
            clearTimeout(this._postReloadGraceTimeoutId);
            this._postReloadGraceTimeoutId = null;
        }
        this._isPostReloadMonitoring = false;
        this._gracePending = false;
        // Keep the flag set — it will be cleared when the error dialog is dismissed
    }

    private _clearReloadFlag(): void {
        sessionStorage.removeItem(LgNetworkErrorService.SESSION_KEY_RELOAD_REASON);
    }

    private _showPostReloadInfoSnackbar(): void {
        this._snackbar.show({
            title: this._lgTranslate.translate("FW.ERROR_TRACKING.RELOAD_INFO_TITLE"),
            text: this._lgTranslate.translate("FW.ERROR_TRACKING.RELOAD_INFO_MESSAGE"),
            autoHide: false
        });
    }

    private _generateGuid(): string {
        // Use crypto.randomUUID() for UUID v4 generation
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        throw new Error("crypto.randomUUID is not available");
    }

    private _logToTracerHub(
        guid: string,
        error: Error | string,
        context?: { url?: string; status?: number }
    ): void {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        const customProperties = {
            errorTrackingId: guid,
            errorType: "network",
            url: context?.url,
            statusCode: context?.status
        };

        this._tracerHub.trackException(errorObj, customProperties);
    }

    private _cleanupOldErrors(): void {
        const cutoff = Date.now() - LgNetworkErrorService.ERROR_RETENTION_MS;
        this._trackedErrors = this._trackedErrors.filter(e => e.timestamp > cutoff);
    }

    private _scheduleModalDisplay(): void {
        // If modal is already visible, don't schedule another
        if (this._isModalVisible) {
            return;
        }

        // If already in cooldown, the existing timeout will handle it
        if (this._cooldownTimeoutId !== null) {
            return;
        }

        // Start cooldown period
        this._cooldownTimeoutId = setTimeout(() => {
            this._showErrorModal();
            this._cooldownTimeoutId = null;
        }, LgNetworkErrorService.COOLDOWN_MS);
    }

    private _showErrorModal(): void {
        const allGuids = this._getRecentGuids();
        if (allGuids.length === 0) {
            return;
        }

        this._isModalVisible = true;

        const displayedGuids = allGuids.slice(0, LgNetworkErrorService.MAX_DISPLAYED_GUIDS);
        const hiddenCount = allGuids.length - displayedGuids.length;

        const title = this._lgTranslate.translate("FW.ERROR_TRACKING.TITLE");

        // Build the reference codes section
        let refSection: string;
        if (displayedGuids.length === 1 && hiddenCount === 0) {
            refSection = `<code style="user-select: all;">${displayedGuids[0]}</code>`;
        } else {
            refSection = displayedGuids
                .map(g => `<code style="user-select: all;">${g}</code>`)
                .join("<br>");
            if (hiddenCount > 0) {
                refSection += `<br>${this._lgTranslate.translate("FW.ERROR_TRACKING.MESSAGE_MORE", { count: hiddenCount })}`;
            }
        }

        const message = this._lgTranslate.translate("FW.ERROR_TRACKING.MESSAGE_PERSISTENT", {
            refs: refSection
        });

        const dialogPromise = this._promptDialog.alert(title, message, {
            allowClose: true,
            columns: 4,
            buttons: [
                {
                    id: "reload",
                    name: "Reload",
                    nameLc: "FW.ERROR_TRACKING.RELOAD",
                    class: "button--primary",
                    isConfirmAction: true
                }
            ]
        });

        dialogPromise
            .then(response => {
                if (response === "reload") {
                    window.location.reload();
                }
            })
            .finally(() => {
                this._isModalVisible = false;
                this._trackedErrors = [];
                this._clearReloadFlag();
            });
    }
}
